import { useState, useEffect } from 'react';
import { Building2, Globe, RefreshCw, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import apiClient from '../utils/axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function CompanyDomainAssignments() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyAssignment, setCompanyAssignment] = useState(null);
  const [companyHistory, setCompanyHistory] = useState([]);
  const [blacklisted, setBlacklisted] = useState([]);

  useEffect(() => {
    fetchBlacklisted();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyAssignment();
      fetchCompanyHistory();
    }
  }, [selectedCompany]);

  const fetchBlacklisted = async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/domain-assignments/blacklisted`);
      setBlacklisted(response.data.blacklisted || []);
    } catch (error) {
      console.error('Error fetching blacklisted assignments:', error);
    }
  };

  const fetchCompanyAssignment = async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/companies/${encodeURIComponent(selectedCompany)}/domain-assignment`);
      setCompanyAssignment(response.data.assignment);
    } catch (error) {
      console.error('Error fetching company assignment:', error);
      setCompanyAssignment(null);
    }
  };

  const fetchCompanyHistory = async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/companies/${encodeURIComponent(selectedCompany)}/domain-history`);
      setCompanyHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching company history:', error);
      setCompanyHistory([]);
    }
  };

  const handleSwitchDomain = async (companyName) => {
    if (!confirm(`Switch domain for ${companyName}? This will pause the current domain and assign a new one.`)) {
      return;
    }

    try {
      await apiClient.post(`${API_BASE}/companies/${encodeURIComponent(companyName)}/switch-domain`, {
        reason: 'manual_switch'
      });
      alert('Domain switched successfully!');
      if (selectedCompany === companyName) {
        fetchCompanyAssignment();
        fetchCompanyHistory();
      }
    } catch (error) {
      console.error('Error switching domain:', error);
      alert(error.response?.data?.message || 'Error switching domain');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 size={24} />
          Company Domain Assignments
        </h2>

        {/* Search Company */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Company
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Enter company name..."
              value={selectedCompany || ''}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => selectedCompany && fetchCompanyAssignment()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Search
            </button>
          </div>
        </div>

        {/* Current Assignment */}
        {companyAssignment && (
          <div className="mb-6 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Current Assignment</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Domain</p>
                <p className="font-medium text-gray-900">{companyAssignment.assigned_domain}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className={`font-medium ${
                  companyAssignment.status === 'active' ? 'text-green-600' :
                  companyAssignment.status === 'paused' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {companyAssignment.status}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Opt-Out Rate</p>
                <p className="font-medium text-gray-900">
                  {parseFloat(companyAssignment.opt_out_rate || 0).toFixed(2)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Emails Sent</p>
                <p className="font-medium text-gray-900">{companyAssignment.emails_sent || 0}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => handleSwitchDomain(selectedCompany)}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
              >
                <RefreshCw size={16} />
                Switch Domain
              </button>
            </div>
          </div>
        )}

        {/* Assignment History */}
        {companyHistory.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Domain History</h3>
            <div className="space-y-2">
              {companyHistory.map((history) => (
                <div key={history.id} className="border border-gray-200 rounded p-3 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">
                      {history.old_domain || 'N/A'} → <span className="font-medium text-gray-900">{history.new_domain}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(history.switched_at).toLocaleString()} • {history.switch_reason}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Blacklisted Assignments */}
        {blacklisted.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-600" />
              Blacklisted Company-Domain Pairs
            </h3>
            <div className="space-y-2">
              {blacklisted.map((assignment) => (
                <div key={assignment.id} className="border border-red-200 bg-red-50 rounded p-3 flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-900">{assignment.company_name}</p>
                    <p className="text-sm text-gray-600">{assignment.assigned_domain}</p>
                    {assignment.blacklist_reason && (
                      <p className="text-xs text-gray-500 mt-1">Reason: {assignment.blacklist_reason}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-red-600">
                      {parseFloat(assignment.opt_out_rate || 0).toFixed(2)}% opt-out rate
                    </p>
                    <p className="text-xs text-gray-500">{assignment.opt_out_count} opt-outs</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!companyAssignment && !selectedCompany && (
          <div className="text-center text-gray-500 py-8">
            <Globe size={48} className="mx-auto mb-4 text-gray-400" />
            <p>Enter a company name to view its domain assignment.</p>
          </div>
        )}
      </div>
    </div>
  );
}

